import React, { useState } from 'react';

const RANGI_VERSION = process.env.REACT_APP_RANGI_VERSION || '2.0.0';
const THIRD_PARTY_NOTICES_URL = `${process.env.PUBLIC_URL || ''}/legal/third-party-notices.txt`;

function ContactIcon({ type }) {
  const common = {
    width: 30,
    height: 30,
    viewBox: '0 0 24 24',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
    'aria-hidden': true,
  };

  if (type === 'mail') {
    return (
      <svg {...common}>
        <path d="M4 6h16v12H4V6Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        <path d="M4 7l8 6 8-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (type === 'support') {
    return (
      <svg {...common}>
        <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L4 17l3 3 5.3-5.3a4 4 0 0 0 5.4-5.4l-2.8 2.8-3-3 2.8-2.8Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (type === 'version') {
    return (
      <svg {...common}>
        <path d="M12 3l8 4v10l-8 4-8-4V7l8-4Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        <path d="M12 8v8M8 10l4-2 4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (type === 'owner') {
    return (
      <svg {...common}>
        <path d="M4 21V8l8-5 8 5v13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9 21v-7h6v7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9 10h.01M12 10h.01M15 10h.01" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <path d="M12 21s7-5.2 7-11a7 7 0 1 0-14 0c0 5.8 7 11 7 11Z" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function InfoRow({ icon, title, children }) {
  return (
    <div style={styles.row}>
      <div style={styles.iconBox}>
        <ContactIcon type={icon} />
      </div>
      <div style={styles.rowText}>
        <div style={styles.rowTitle}>{title}</div>
        <div style={styles.rowValue}>{children}</div>
      </div>
    </div>
  );
}

export default function ProgramInfo() {
  const [isNoticesOpen, setIsNoticesOpen] = useState(false);
  const [noticesText, setNoticesText] = useState('');
  const [noticesLoading, setNoticesLoading] = useState(false);
  const [noticesError, setNoticesError] = useState('');

  const openNotices = async () => {
    setIsNoticesOpen(true);

    if (noticesText || noticesLoading) {
      return;
    }

    try {
      setNoticesLoading(true);
      setNoticesError('');

      const response = await fetch(THIRD_PARTY_NOTICES_URL, {
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const text = await response.text();
      setNoticesText(text);
    } catch (error) {
      setNoticesError('Не удалось загрузить текст сторонних компонентов и лицензий.');
    } finally {
      setNoticesLoading(false);
    }
  };

  const closeNotices = () => {
    setIsNoticesOpen(false);
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.title}>О программе</h1>
        </div>

        <div style={styles.infoList}>
          <InfoRow icon="version" title="Версия приложения">
            <span style={styles.versionBadge}>{RANGI_VERSION}</span>
          </InfoRow>

          <InfoRow icon="owner" title="Правообладатель">
            ООО &quot;АЙРИУМ&quot;
          </InfoRow>

          <InfoRow icon="support" title="Техподдержка">
            <a style={styles.link} href="mailto:support@ai-rium.ru">
              support@ai-rium.ru
            </a>
            <span style={styles.muted}> пн–пт 9–18</span>
          </InfoRow>
        </div>

        <div style={styles.footer}>
          <button
            type="button"
            style={styles.siteButton}
            onClick={openNotices}
          >
            Сторонние компоненты
          </button>
        </div>
      </div>

      {isNoticesOpen && (
        <div style={styles.modalOverlay} onClick={closeNotices}>
          <div style={styles.modalCard} onClick={(event) => event.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Сторонние компоненты</h2>
              <button
                type="button"
                style={styles.modalCloseButton}
                onClick={closeNotices}
                aria-label="Закрыть"
              >
                ×
              </button>
            </div>

            <div style={styles.modalBody}>
              {noticesLoading && (
                <div style={styles.modalStateText}>Загрузка...</div>
              )}

              {!noticesLoading && noticesError && (
                <div style={styles.modalErrorText}>{noticesError}</div>
              )}

              {!noticesLoading && !noticesError && (
                <pre style={styles.noticesText}>{noticesText}</pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    boxSizing: 'border-box',
    padding: '48px 18px 90px',
    background: 'rgb(244, 226, 255)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    width: 'min(980px, 100%)',
    boxSizing: 'border-box',
    padding: '48px 72px',
    borderRadius: 36,
    background: 'rgba(255,255,255,0.94)',
    boxShadow: '0 28px 90px rgba(125, 66, 180, 0.18)',
    border: '1px solid rgba(255,255,255,0.75)',
  },
  header: {
    marginBottom: 36,
  },
  title: {
    margin: 0,
    color: '#171426',
    fontSize: 34,
    fontWeight: 800,
    letterSpacing: '-0.03em',
  },
  infoList: {
    display: 'flex',
    flexDirection: 'column',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 28,
    padding: '24px 0',
    borderBottom: '1px solid rgba(80, 70, 90, 0.12)',
  },
  iconBox: {
    flex: '0 0 auto',
    width: 76,
    height: 76,
    borderRadius: 22,
    color: '#534168',
    background: 'linear-gradient(135deg, #f4dbff 0%, #f1e8ff 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowText: {
    minWidth: 0,
  },
  rowTitle: {
    marginBottom: 8,
    color: '#9a8cb3',
    fontSize: 18,
    fontWeight: 800,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  rowValue: {
    color: '#171426',
    fontSize: 28,
    fontWeight: 500,
    lineHeight: 1.3,
    wordBreak: 'break-word',
  },
  versionBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '8px 16px',
    borderRadius: 999,
    color: '#ffffff',
    background: 'linear-gradient(135deg, #BD23DB 0%, #7C3AED 100%)',
    fontSize: 24,
    fontWeight: 800,
  },
  link: {
    color: '#BD23DB',
    textDecoration: 'none',
    fontWeight: 700,
  },
  muted: {
    color: '#9a8cb3',
    fontSize: 24,
    fontWeight: 600,
  },
  footer: {
    marginTop: 34,
    display: 'flex',
    justifyContent: 'flex-start',
  },
  siteButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 52,
    padding: '0 26px',
    border: 0,
    borderRadius: 16,
    color: '#ffffff',
    background: 'linear-gradient(135deg, #BD23DB 0%, #7C3AED 100%)',
    fontSize: 17,
    fontWeight: 800,
    textDecoration: 'none',
    boxShadow: '0 14px 34px rgba(189, 35, 219, 0.28)',
    cursor: 'pointer',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 10000,
    boxSizing: 'border-box',
    padding: 24,
    background: 'rgba(23, 20, 38, 0.42)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalCard: {
    width: 'min(980px, 100%)',
    maxHeight: '86vh',
    boxSizing: 'border-box',
    borderRadius: 28,
    background: '#ffffff',
    boxShadow: '0 28px 90px rgba(23, 20, 38, 0.28)',
    overflow: 'hidden',
  },
  modalHeader: {
    minHeight: 72,
    padding: '0 26px',
    borderBottom: '1px solid rgba(80, 70, 90, 0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 18,
  },
  modalTitle: {
    margin: 0,
    color: '#171426',
    fontSize: 24,
    fontWeight: 800,
    letterSpacing: '-0.02em',
  },
  modalCloseButton: {
    width: 42,
    height: 42,
    border: 0,
    borderRadius: 14,
    color: '#534168',
    background: '#f4e2ff',
    fontSize: 32,
    lineHeight: '38px',
    cursor: 'pointer',
  },
  modalBody: {
    padding: 26,
  },
  noticesText: {
    margin: 0,
    maxHeight: 'min(62vh, 620px)',
    overflowY: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    color: '#171426',
    fontFamily: 'inherit',
    fontSize: 15,
    lineHeight: 1.55,
  },
  modalStateText: {
    color: '#817799',
    fontSize: 16,
    fontWeight: 600,
  },
  modalErrorText: {
    color: '#b42318',
    fontSize: 16,
    fontWeight: 700,
  },
};